import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import SpeechTextArea from "@/components/ui/speech-text-area";
import { FileInput } from "@/components/lesson-lab/FileInput";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Control } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { memo, useState, useCallback } from "react";

interface SubjectInputProps {
  control: Control<any>;
  onForceUpdate: () => void;
  tool: "lessonLab" | "catchupCoach" | "communicationGenerator";
}

export const SubjectInput = memo(function SubjectInput({
  control,
  onForceUpdate,
  tool = "lessonLab",
}: SubjectInputProps) {
  const { t } = useTranslation([tool, "common"]);
  const [focused, setFocused] = useState(false);
  const handleFocus = useCallback(() => setFocused(true), []);
  const handleBlur = useCallback(() => setFocused(false), []);

  return (
    <div>
      <FormField
        control={control}
        name="subject"
        render={({ field }) => (
          <FormItem className="flex flex-col" id="subject-form-item">
            <FormLabel>{t("subject.label")}</FormLabel>
            <FormDescription
              id="subject-form-descriptor"
              className="!text-content !text-light-grey mb-4"
            >
              {t("subject.description")}
            </FormDescription>
            <SpeechTextArea
              {...field}
              onFocus={handleFocus}
              onBlur={() => {
                field.onBlur();
                handleBlur();
              }}
              className="!rounded-b-none focus-visible:!outline-none focus-visible:ring-0 focus-visible:!border-r-2 focus-visible:!border-t-2 focus-visible:!border-l-2 focus-visible:!border-primary"
              placeholder={[
                t("subject.placeholder.1"),
                t("subject.placeholder.2"),
                t("subject.placeholder.3"),
                t("subject.placeholder.4"),
                t("subject.placeholder.5"),
                t("subject.placeholder.6"),
              ]}
            />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="files"
        render={({ field }) => (
          <>
            <FileInput
              className={`rounded-t-none ${focused ? "border-r-2 border-l-2 border-b-2 border-primary" : ""}`}
              type="file"
              accept="abw,csv,doc,docm,docx,dot,dotx,dps,et,hwp,lwp,md,odp,ods,odt,pdf,pps,ppsx,ppt,pptm,pptx,rst,rtf,txt,wpd,wps,xls,xlsm,xlsx,xps,zabw"
              multiple
              onBlurCapture={(e) => {
                const files = Array.from(e.target.files || []);
                field.onChange(files);
                onForceUpdate();
              }}
              onFilesChange={(files) => {
                setTimeout(() => {
                  field.onChange(files);
                  onForceUpdate();
                }, 0);
              }}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
            />
            <TooltipProvider>
              <FormDescription className="mt-2 !text-content !text-light-grey">
                <Trans
                  i18nKey="subject.fileDescription"
                  ns="lessonLab"
                  components={{
                    1: (
                      <Tooltip>
                        <TooltipTrigger
                          className="underline text-primary decoration-dashed"
                          type="button"
                        >
                          here.
                        </TooltipTrigger>
                        <TooltipContent>
                          <div>
                            <ul className="grid grid-cols-3 gap-x-4">
                              <li>.abw</li>
                              <li>.csv</li>
                              <li>.doc</li>
                              <li>.docm</li>
                              <li>.docx</li>
                              <li>.dot</li>
                              <li>.dotx</li>
                              <li>.dps</li>
                              <li>.et</li>
                              <li>.hwp</li>
                              <li>.lwp</li>
                              <li>.md</li>
                              <li>.odp</li>
                              <li>.ods</li>
                              <li>.odt</li>
                              <li>.pdf</li>
                              <li>.pps</li>
                              <li>.ppsx</li>
                              <li>.ppt</li>
                              <li>.pptm</li>
                              <li>.pptx</li>
                              <li>.rst</li>
                              <li>.rtf</li>
                              <li>.txt</li>
                              <li>.wpd</li>
                              <li>.wps</li>
                              <li>.xls</li>
                              <li>.xlsm</li>
                              <li>.xlsx</li>
                              <li>.xps</li>
                              <li>.zabw</li>
                            </ul>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    ),
                  }}
                />
              </FormDescription>
            </TooltipProvider>
          </>
        )}
      />
    </div>
  );
});
